/**
 * When a visitor would like to meet, read from their own words.
 *
 * <h3>A preference, never a filter</h3>
 *
 * <p>Somebody saying "Wednesday" is telling us what would suit them, not setting a condition. A
 * timeline that <em>narrowed</em> the search would hand back nothing when Wednesday happened to be
 * full, and make a host look unavailable when they are not — throwing away good times because of a
 * preference the visitor would happily have bent.</p>
 *
 * <p>So the rule is one sentence: <b>the timeline never narrows what is asked for. It only ever
 * widens the window so the preferred time is certainly in it, and then decides what to show
 * first.</b> Everything that comes back is still offered.</p>
 *
 * <h3>Why it never leaves the page</h3>
 *
 * <p>A span carries a start and an end and never a score (D-11, D-91). The preference therefore
 * cannot be a ranking sent back from the host's side — and it should not be. It is a fact about the
 * visitor's own week, it belongs to them, and the host is never told what somebody would have
 * preferred.</p>
 */
export interface Preference {
  /** Inclusive, ISO date. Null when they expressed no preference, which is a real answer. */
  from: string | null;
  to: string | null;
  /** What to call it back to them. Null when there is nothing to call back. */
  label: string | null;
  /** True when they said something we could not read. Never a refusal, and never narrowing. */
  unread: boolean;
}

export const NO_PREFERENCE: Preference = { from: null, to: null, label: null, unread: false };

const DAYS = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
] as const;

const SHORT: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, tues: 2, wed: 3, weds: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6,
};

/** Anything that means "I don't mind". A first-class answer, not a failure to answer. */
const WHENEVER = /\b(?:whenever|any\s*time|anytime|no\s*rush|no\s*preference|doesn'?t\s*matter|flexible|up\s*to\s*(?:you|them))\b/i;

/**
 * Read a timeline out of what they typed.
 *
 * <p>Deliberately small and deterministic. Timeline negotiation past this parse stays deferred, and
 * a phrase we cannot read costs the visitor nothing: it becomes no preference, and says so.</p>
 */
export function readWhen(text: string, now: Date = new Date()): Preference {
  const said = text.trim().toLowerCase();
  if (!said) return NO_PREFERENCE;
  if (WHENEVER.test(said)) return NO_PREFERENCE;

  if (/\btoday\b/.test(said)) return on(now, "today");
  if (/\btomorrow\b/.test(said)) return on(shift(now, 1), "tomorrow");

  if (/\bnext\s+week\b/.test(said)) {
    const monday = startOfNextWeek(now);
    return span(monday, shift(monday, 6), "next week");
  }
  if (/\b(?:this\s+week|end\s+of\s+(?:the\s+)?week|eow)\b/.test(said)) {
    return span(now, endOfThisWeek(now), "this week");
  }
  if (/\b(?:this\s+month|end\s+of\s+(?:the\s+)?month)\b/.test(said)) {
    return span(now, endOfThisMonth(now), "this month");
  }

  // "in 3 days", "within a week", "in the next 2 weeks"
  const within = /\b(?:in|within)\s+(?:the\s+next\s+)?(\d{1,2}|a|an|one|two|three)\s*(day|week)s?\b/.exec(said);
  if (within) {
    const n = wordToNumber(within[1]);
    const days = within[2] === "week" ? n * 7 : n;
    if (days > 0) {
      return span(now, shift(now, days), days === 1 ? "tomorrow" : `the next ${days} days`);
    }
  }

  const day = namedDay(said);
  if (day !== null) {
    const target = nextOccurrenceOf(day, now);
    return on(target, DAYS[day][0].toUpperCase() + DAYS[day].slice(1));
  }

  // They said something, and we could not read it. Not their problem and not a refusal.
  return { ...NO_PREFERENCE, unread: true };
}

/**
 * The window to actually ask about.
 *
 * <p>The union of the ordinary two weeks and anything the visitor preferred, so a preference can
 * only ever <b>widen</b> what is requested. The host's own horizon and the 31-day query cap still
 * apply on the far side and are not this function's business.</p>
 */
export function windowFor(preference: Preference, now: Date = new Date()): { start: string; end: string } {
  const start = isoDate(now);
  const ordinaryEnd = isoDate(shift(now, 14));
  if (!preference.to) return { start, end: ordinaryEnd };
  return { start, end: preference.to > ordinaryEnd ? preference.to : ordinaryEnd };
}

/** True when this instant falls inside what they said would suit them. */
export function prefers(preference: Preference, iso: string): boolean {
  if (!preference.from || !preference.to) return false;
  const day = iso.slice(0, 10);
  return day >= preference.from && day <= preference.to;
}

/** What to say back, so a misread is caught before a list of useless times rather than after. */
export function readBack(preference: Preference): string | null {
  if (preference.unread) {
    return "I'll look at the next couple of weeks — say a date if you meant something else.";
  }
  if (!preference.label) return null;
  return `I'll put ${preference.label} first.`;
}

// ------------------------------------------------------------------ the arithmetic

function wordToNumber(word: string): number {
  if (/^\d+$/.test(word)) return Number(word);
  return { a: 1, an: 1, one: 1, two: 2, three: 3 }[word] ?? 0;
}

function namedDay(said: string): number | null {
  for (let i = 0; i < DAYS.length; i += 1) {
    if (new RegExp(`\\b${DAYS[i]}\\b`).test(said)) return i;
  }
  for (const [abbrev, index] of Object.entries(SHORT)) {
    if (new RegExp(`\\b${abbrev}\\b`).test(said)) return index;
  }
  return null;
}

/**
 * The coming one, never today.
 *
 * <p>Somebody saying "Wednesday" on a Wednesday means the next one. Reading it as today would offer
 * them a morning that has mostly gone.</p>
 */
function nextOccurrenceOf(weekday: number, now: Date): Date {
  const ahead = (weekday - now.getDay() + 7) % 7;
  return shift(now, ahead === 0 ? 7 : ahead);
}

function startOfNextWeek(now: Date): Date {
  return shift(now, ((1 - now.getDay() + 7) % 7) || 7);
}

function endOfThisWeek(now: Date): Date {
  return shift(now, (7 - now.getDay()) % 7);
}

function endOfThisMonth(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
}

function shift(from: Date, days: number): Date {
  const out = new Date(from);
  out.setDate(out.getDate() + days);
  return out;
}

function on(day: Date, label: string): Preference {
  return { from: isoDate(day), to: isoDate(day), label, unread: false };
}

function span(from: Date, to: Date, label: string): Preference {
  return { from: isoDate(from), to: isoDate(to), label, unread: false };
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
